import {
  Activity,
  Bell,
  CalendarDays,
  ChevronDown,
  ClipboardCheck,
  FileBarChart,
  GraduationCap,
  Home,
  KeyRound,
  LogOut,
  Menu,
  Medal,
  Megaphone,
  Search,
  Settings,
  ShieldCheck,
  Trophy,
  Sparkles,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useApp } from '../domain/AppContext';
import { formatClassCodesInText, formatClassName } from '../domain/classCode';
import { getPhaseDateRange, isOlympiadVisibleToStudent } from '../domain/rules';
import type { AppNotification } from '../domain/types';
import { Button, Dialog, IconButton, SearchField } from './ui';

interface NavigationItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  badge?: number;
}

const studentNavigation: NavigationItem[] = [
  { to: '/dashboard', label: 'Início', icon: Home },
  { to: '/mural-de-destaques', label: 'Destaques', icon: Sparkles },
  { to: '/olimpiadas', label: 'Olimpíadas', icon: Trophy },
  { to: '/calendario', label: 'Calendário', icon: CalendarDays },
  { to: '/resultados', label: 'Resultados', icon: Medal },
  { to: '/inscricoes', label: 'Minhas inscrições', icon: ClipboardCheck },
  { to: '/configuracoes', label: 'Configurações', icon: Settings },
];

const adminNavigation: NavigationItem[] = [
  { to: '/dashboard', label: 'Visão geral', icon: Home },
  { to: '/olimpiadas', label: 'Olimpíadas', icon: Trophy },
  { to: '/mural-de-destaques', label: 'Mural de Destaques', icon: Sparkles },
  { to: '/calendario', label: 'Calendário', icon: CalendarDays },
  { to: '/resultados', label: 'Resultados', icon: Medal },
  { to: '/inscricoes', label: 'Inscrições', icon: ClipboardCheck },
  { to: '/credenciais', label: 'Credenciais', icon: KeyRound },
  { to: '/instituicao', label: 'Instituição', icon: ShieldCheck },
  { to: '/relatorios', label: 'Relatórios', icon: FileBarChart },
  { to: '/comunicados', label: 'Comunicados', icon: Megaphone },
  { to: '/estudantes', label: 'Estudantes', icon: GraduationCap },
  { to: '/professores', label: 'Professores', icon: Users },
  { to: '/atividades', label: 'Atividades', icon: Activity },
  { to: '/configuracoes', label: 'Configurações', icon: Settings },
];

const coordinatorNavigation = adminNavigation.filter((item) => !['/professores', '/atividades', '/comunicados'].includes(item.to));

export function AppShell() {
  const { profile, data, logout, changeActiveUnit } = useApp();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!profile) return null;
  const isStudent = profile.role === 'student';
  const can = (permission: string) => profile.permissions.includes('*') || profile.permissions.includes(permission);
  const navigation = isStudent ? studentNavigation : profile.role === 'teacher' && !profile.isSystemManager ? coordinatorNavigation.filter((item) => {
    if (item.to === '/inscricoes') return can('enrollments:view');
    if (item.to === '/relatorios') return can('reports:view');
    if (item.to === '/credenciais') return can('credentials:view');
    return true;
  }) : adminNavigation;
  const deadlineNotifications = isStudent && data ? data.enrollments.flatMap((enrollment) => {
    if (!enrollment.studentIds.includes(profile.id) || enrollment.status === 'cancelled') return [];
    const olympiad = data.olympiads.find((item) => item.id === enrollment.olympiadId);
    const phaseIndex = Math.min(Math.max(enrollment.currentStep - 1, 0), Math.max((olympiad?.phases.length ?? 1) - 1, 0));
    const phase = olympiad?.phases[phaseIndex];
    const examDate = phase ? getPhaseDateRange(phase, profile.className).date : olympiad?.examDate;
    if (!olympiad || !examDate) return [];
    const reminders = [] as AppNotification[];
    const exam = new Date(`${examDate}T09:00:00`);
    const daysToExam = Math.ceil((exam.getTime() - Date.now()) / 86400000);
    if (daysToExam >= 0 && daysToExam <= 2) reminders.push({ id: `exam-deadline-${enrollment.id}`, title: `A prova da ${olympiad.acronym} está se aproximando`, description: daysToExam === 0 ? 'A prova acontece hoje.' : `Faltam ${daysToExam} dias para a prova. Confira o calendário e o regulamento.`, category: 'Prazos', createdAt: new Date().toLocaleDateString('pt-BR'), read: false });
    const deadline = new Date(`${olympiad.enrollmentEnd}T23:59:00`);
    const daysToDeadline = Math.ceil((deadline.getTime() - Date.now()) / 86400000);
    if (daysToDeadline >= 0 && daysToDeadline <= 2) reminders.push({ id: `enrollment-deadline-${enrollment.id}`, title: `O prazo de inscrição da ${olympiad.acronym} está terminando`, description: daysToDeadline === 0 ? 'O prazo termina hoje.' : `Faltam ${daysToDeadline} dias para o encerramento das inscrições.`, category: 'Prazos', createdAt: new Date().toLocaleDateString('pt-BR'), read: false });
    return reminders;
  }) : [];
  const visibleNotifications = [...deadlineNotifications, ...(data?.notifications ?? [])].filter((item) => item.category !== 'Materiais' && !`${item.title} ${item.description}`.toLocaleLowerCase('pt-BR').includes('material'));
  const unread = visibleNotifications.filter((item) => !item.read).length;
  const pending = data?.enrollments.filter((item) => item.status === 'pending' || item.status === 'needs_info').length ?? 0;
  const availableUnits = profile.isSystemManager
    ? (data?.institution.units ?? [])
    : (data?.institution.units ?? []).filter((unit) => profile.unitIds?.includes(unit.id));

  const results = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('pt-BR');
    if (!normalized || !data) return [];
    const currentStudent = data.students.find((item) => item.id === profile.id);
    const olympiads = data.olympiads.filter((item) => (profile.role !== 'student' || Boolean(currentStudent && isOlympiadVisibleToStudent(item, currentStudent))) && `${item.acronym} ${item.name} ${item.area}`.toLocaleLowerCase('pt-BR').includes(normalized)).slice(0, 4).map((item) => ({ id: item.id, type: 'Olimpíada', title: `${item.acronym} 2026`, detail: item.name, to: `/olimpiadas/${item.slug}` }));
    const students = profile.role === 'student' ? [] : data.students.filter((item) => `${item.name} ${item.ra} ${item.className} ${formatClassName(item.className)}`.toLocaleLowerCase('pt-BR').includes(normalized)).slice(0, 4).map((item) => ({ id: item.id, type: 'Estudante', title: item.name, detail: `${formatClassName(item.className)} · RA ${item.ra}`, to: `/estudantes/${item.id}` }));
    return [...olympiads, ...students];
  }, [data, profile.id, profile.role, query]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigationContent = (
    <>
      <div className="sidebar-brand">
        <img src="./brand/icon-05-sangria-2048.png" alt="" />
        <div><strong>Oficina Sapiens</strong><span>Colégios Univap</span></div>
      </div>
      <nav className="sidebar-nav" aria-label="Navegação principal">
        {navigation.map((item) => {
          const Icon = item.icon;
          const badge = item.to === '/inscricoes' ? pending : item.badge;
          return (
            <NavLink key={item.to} to={item.to} onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>
              <Icon size={20} />
              <span>{item.label}</span>
              {badge ? <em>{badge}</em> : null}
            </NavLink>
          );
        })}
      </nav>
      <div className="sidebar-user">
        <span className="avatar">{profile.name.split(' ').map((part) => part[0]).slice(0, 2).join('')}</span>
        <div><strong>{profile.name}</strong><span>{profile.isSystemManager ? 'Gestor de Sistema' : profile.role === 'teacher' ? 'Coordenador' : formatClassName(profile.className)}</span></div>
        <IconButton label="Sair" onClick={handleLogout}><LogOut size={18} /></IconButton>
      </div>
    </>
  );

  return (
    <div className={`app-shell ${isStudent ? 'student-shell' : 'admin-shell'}`}>
      {!isStudent ? <aside className="desktop-sidebar">{navigationContent}</aside> : null}
      {menuOpen ? <div className="mobile-sidebar-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target) setMenuOpen(false); }}><aside className="mobile-sidebar"><IconButton label="Fechar menu" className="sidebar-close" onClick={() => setMenuOpen(false)}><X size={20} /></IconButton>{navigationContent}</aside></div> : null}

      <div className="app-workspace">
        <header className="app-topbar">
          <div className="topbar-start">
            {!isStudent ? <IconButton label="Abrir menu" className="menu-trigger" onClick={() => setMenuOpen(true)}><Menu size={21} /></IconButton> : null}
            {isStudent ? <NavLink to="/dashboard" className="topbar-brand"><img src="./brand/icon-05-sangria-2048.png" alt="" /><strong>Oficina Sapiens</strong></NavLink> : null}
          </div>
          {isStudent ? <nav className="student-topnav" aria-label="Navegação do estudante">{studentNavigation.map((item) => <NavLink key={item.to} to={item.to} className={({ isActive }) => isActive ? 'active' : ''}>{item.label}</NavLink>)}</nav> : null}
          <div className="topbar-actions">
            {!isStudent && availableUnits.length > 1 ? <label className="topbar-unit-switch"><span>Unidade</span><select aria-label="Trocar unidade ativa" value={profile.unitId} onChange={(event) => void changeActiveUnit(event.target.value)}>{availableUnits.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}</select></label> : null}
            <button className="global-search-trigger" type="button" onClick={() => setSearchOpen(true)}><Search size={17} /><span>Buscar</span><kbd>⌘K</kbd></button>
            <IconButton label={`Notificações${unread ? `, ${unread} não lidas` : ''}`} className="notification-trigger" onClick={() => setNotificationOpen(true)}><Bell size={20} />{unread ? <em>{unread}</em> : null}</IconButton>
            <button className="account-trigger" type="button" onClick={() => navigate('/perfil')} aria-label={`Abrir perfil de ${profile.name}`}><span className="avatar">{profile.name.split(' ').map((part) => part[0]).slice(0, 2).join('')}</span><span>{profile.shortName}</span><ChevronDown size={15} /></button>
          </div>
        </header>

        <main id="conteudo-principal" className="app-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      {isStudent ? <nav className="mobile-bottom-nav" aria-label="Navegação inferior">{studentNavigation.slice(0, 3).concat([{ to: '/perfil', label: 'Perfil', icon: UserRound }]).map((item) => { const Icon = item.icon; return <NavLink key={item.to} to={item.to} className={({ isActive }) => isActive ? 'active' : ''}><Icon size={21} /><span>{item.label}</span></NavLink>; })}</nav> : null}

      <Dialog
        open={searchOpen}
        title="Busca global"
        description={isStudent ? 'Encontre olimpíadas em um só lugar.' : 'Encontre estudantes e olimpíadas em um só lugar.'}
        onClose={() => { setSearchOpen(false); setQuery(''); }}
        size="large"
      >
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder={isStudent ? 'Buscar olimpíada...' : 'Buscar estudante por nome ou RA, olimpíada ou sigla...'}
          label={isStudent ? 'Buscar olimpíada' : 'Buscar estudante ou olimpíada'}
        />
        <div className="command-results">
          {!query ? <p className="command-hint">Digite para buscar. Use as setas para navegar e Enter para abrir.</p> : results.length ? results.map((result) => <button type="button" key={`${result.type}-${result.id}`} onClick={() => { navigate(result.to); setSearchOpen(false); setQuery(''); }}><span>{result.type}</span><strong>{result.title}</strong><small>{result.detail}</small></button>) : <p className="command-hint">Nenhum resultado encontrado. Tente outro termo.</p>}
        </div>
      </Dialog>

      <Dialog open={notificationOpen} title="Central de notificações" description={`${unread} notificações não lidas`} onClose={() => setNotificationOpen(false)} size="medium">
        <div className="notification-list">
          {visibleNotifications.map((item) => <article key={item.id} className={!item.read ? 'unread' : ''}><span className="notification-dot" /><div><strong>{formatClassCodesInText(item.title)}</strong><p>{formatClassCodesInText(item.description)}</p><small>{item.createdAt} · {item.category}</small></div></article>)}
        </div>
        <Button variant="secondary" onClick={() => { setNotificationOpen(false); if (!isStudent) navigate('/comunicados'); }}>{isStudent ? 'Fechar' : 'Ver todas as notificações'}</Button>
      </Dialog>
    </div>
  );
}
